import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ArrowLeft, Hammer, Radio, Users } from 'lucide-react';
import {
  CurrentShot,
  fetchLiveGameDetail,
  LiveGameDetail as LiveGameDetailData,
  LiveGameEndScore,
  LiveGamePlayerScore,
  LiveGameTeamScore,
} from '../api/liveGames';
import { ApiError } from '../api/client';
import { CurlAssessment, RockColor, ShotType, Weight } from '../types/curling';

/** Solid fill + readable text color for a team's name block — an arena board
 * paints the team's own rock color behind their name, not a small swatch. */
const ROCK_COLOR_BLOCK: Record<RockColor, { bg: string; text: string }> = {
  red: { bg: '#dc2626', text: '#ffffff' },
  yellow: { bg: '#fbbf24', text: '#1c1917' },
  blue: { bg: '#2563eb', text: '#ffffff' },
  green: { bg: '#16a34a', text: '#ffffff' },
  white: { bg: '#f4f4f5', text: '#1c1917' },
};

const REFRESH_MS = 15_000;

/** Ends 1-8 get their own numbered column; anything past that (a tie forcing
 * extra ends) is folded into one "E" column, the same way a physical arena
 * board only has one extra-end tile, not a slot per possible tiebreaker. */
const REGULATION_ENDS = 8;

/**
 * The header row plus the two team rows' rendered height, in px — fixed by
 * the (constant) padding and font sizes on those cells, never by the data
 * (end count only ever changes the grid's width, not its height). Used to
 * size the left ad block's logo to exactly match: `height: 100%` on the
 * image does not reliably resolve here in any layout mode (flex, grid, or
 * table-display) because the row's own height only ever comes from its
 * content, which CSS still treats as "auto" for percentage-resolution
 * purposes even once visually stretched to fill the row. A real pixel
 * constant sidesteps that rather than fighting it.
 */
const SCOREBOARD_ROW_HEIGHT_PX = 132;

/**
 * Standard single-hammer rule: the team that didn't score an end gets hammer
 * next; a blanked end (nobody scored) leaves it where it was. Approximate —
 * this app's own two-player variant handling (see LiveGame.tsx) isn't worth
 * replicating for a read-only spectator board — and only shown for a game
 * still in progress, same as a broadcast graphic drops the hammer mark once
 * the game's over.
 */
function currentHammerTeam(game: LiveGameDetailData): 'team1' | 'team2' | undefined {
  if (game.complete) return undefined;
  let hammer = game.startsWithHammer;
  if (!hammer) return undefined;
  for (const end of game.ends) {
    if (end.team1Score === 0 && end.team2Score === 0) continue;
    hammer = end.team1Score > 0 ? 'team2' : 'team1';
  }
  return hammer;
}

/**
 * "x" for a blanked end (nobody scored), the number for a played end, blank for one not
 * reached yet. The recorder's client pre-creates every end up to the configured count
 * at game setup, so `end` exists in the data well before it's actually played — only
 * `completed` distinguishes "reached and blanked" from "not reached yet".
 */
function endTileText(end: LiveGameEndScore | undefined, myScore: number, otherScore: number): string {
  if (!end || !end.completed) return '';
  if (myScore === 0 && otherScore === 0) return 'x';
  return String(myScore);
}

/** e.g. "7:03 PM" — matches the viewer's local time, not the club's. */
function formatStartTime(epochMs: number): string {
  return new Date(epochMs).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

/**
 * The end currently being played, or null once the game is final. The recording
 * client pre-creates every end up to the configured count at game setup, so
 * `ends.length` is constant from the start and can't tell "current" from "not reached
 * yet" — the first not-completed end is the one actually being played. Shared by the
 * scoreboard's current-end highlight and the rock tracker below it.
 */
function currentEndNumber(game: LiveGameDetailData): number | null {
  if (game.complete) return null;
  return game.ends.find(end => !end.completed)?.number ?? null;
}

/**
 * A proper baseball-style curling scoreboard, styled after the physical
 * arena boards curling broadcasts use: a light gray board, black grid rule lines,
 * each team's own rock color painted behind their name, "x" for a blanked
 * end, and a hammer icon marking who throws last next end.
 */
function ScoreboardTable({ game }: { game: LiveGameDetailData }) {
  const endNumbers = Array.from({ length: REGULATION_ENDS }, (_, i) => i + 1);
  const endsByNumber = new Map(game.ends.map(end => [end.number, end]));
  const extraEnds = game.ends.filter(end => end.number > REGULATION_ENDS);
  const hasExtraEnd = extraEnds.length > 0;
  const extraEndTotal = (pick: (end: LiveGameEndScore) => number) =>
    extraEnds.reduce((sum, end) => sum + pick(end), 0);

  // The end currently being played, highlighted like a marked tile — never shown once
  // the game is final, and folded into the E column past regulation.
  const currentEnd = currentEndNumber(game);
  const isCurrentEndColumn = (n: number) => n === currentEnd;
  const isCurrentExtraEndColumn = currentEnd !== null && currentEnd > REGULATION_ENDS;

  const hammerTeam = currentHammerTeam(game);

  const headerCellBase = "border-2 border-black text-[#1c1917] text-sm font-bold uppercase tracking-wide py-2";
  const scoreCellBase = "border-2 border-black text-center py-2 text-[#1c1917] font-bold text-xl";
  const numberFont = { fontFamily: "'Barlow Condensed', sans-serif" } as const;

  const rows = [
    {
      side: 'team1' as const,
      name: game.team1Name,
      color: game.team1RockColor,
      total: game.team1TotalScore,
      other: game.team2TotalScore,
      score: (e: LiveGameEndScore) => e.team1Score,
      otherScore: (e: LiveGameEndScore) => e.team2Score,
    },
    {
      side: 'team2' as const,
      name: game.team2Name,
      color: game.team2RockColor,
      total: game.team2TotalScore,
      other: game.team1TotalScore,
      score: (e: LiveGameEndScore) => e.team2Score,
      otherScore: (e: LiveGameEndScore) => e.team1Score,
    },
  ];

  return (
    <div
      className="rounded-md p-3 shadow-2xl"
      style={{ background: '#e5e5e5', border: '4px solid #1c1917' }}
    >
      {/* Top ad block — sponsor space; just the club's own name for now, per
          the product decision that these panels get sold to sponsors later.
          Butted directly against the grid below (no margin) so it reads as
          one connected board, not a separate floating banner. */}
      <div className="text-center py-2 border-2 border-b-0 border-black" style={{ background: '#1c1917' }}>
        <span className="text-white font-bold uppercase tracking-[0.2em] text-lg" style={numberFont}>
          Black Rock Curling
        </span>
      </div>

      <div className="flex items-stretch">
        {/* Left ad block — sponsor space; same reservation as the top banner.
            SCOREBOARD_ROW_HEIGHT_PX is a real pixel height, not a percentage:
            `height: 100%` never reliably resolves against a table/flex row
            whose own height only comes from its content (it's still "auto"
            in the CSS sense, even once visually stretched) - every attempt
            at that collapsed the image to ~0 or blew it up to its native
            1275x1650. The height goes on this container, not the image, with
            box-sizing: border-box so its own border is absorbed inside that
            132px rather than added on top (which is what threw the image's
            bottom edge a few px past the table's) - now the container has a
            real specified height, h-full on the image resolves against it
            normally. The row's actual height is deterministic (the same
            fixed paddings/font sizes regardless of end count or score), so a
            real measured constant is the reliable fix, not a hack. */}
        <div
          className="flex-shrink-0 border-2 border-black overflow-hidden"
          style={{ height: SCOREBOARD_ROW_HEIGHT_PX, boxSizing: 'border-box' }}
        >
          <img
            src="/sponsors/black-rock-curling-leaf.jpg"
            alt="Black Rock Curling"
            className="h-full w-auto block"
          />
        </div>

        <div className="overflow-x-auto flex-1">
          <table className="border-collapse w-full" style={numberFont}>
            <thead>
            <tr>
              <th className={`${headerCellBase} text-left pl-3 pr-4 min-w-[150px] bg-[#e5e5e5]`}>Team</th>
              <th className={`${headerCellBase} w-[48px] bg-[#e5e5e5]`}>
                <Hammer className="w-4 h-4 mx-auto" />
              </th>
              {endNumbers.map(number => (
                <th
                  key={number}
                  className={`${headerCellBase} w-[48px] ${isCurrentEndColumn(number) ? 'bg-amber-300' : 'bg-[#e5e5e5]'}`}
                >
                  {number}
                </th>
              ))}
              <th className={`${headerCellBase} w-[48px] ${isCurrentExtraEndColumn ? 'bg-amber-300' : 'bg-[#e5e5e5]'}`}>
                E
              </th>
              <th className={`${headerCellBase} w-[70px] bg-[#d4d4d4]`}>Total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const block = ROCK_COLOR_BLOCK[row.color ?? 'white'];
              return (
                <tr key={row.side}>
                  <td className={`${scoreCellBase} text-left pl-3 pr-4 text-base`} style={{ background: block.bg, color: block.text }}>
                    <span className="truncate block">{row.name}</span>
                  </td>
                  <td className={`${scoreCellBase} ${hammerTeam === row.side ? 'bg-amber-100' : 'bg-white'}`}>
                    {hammerTeam === row.side && (
                      <span title="Throws last next end">
                        <Hammer className="w-4 h-4 mx-auto" />
                      </span>
                    )}
                  </td>
                  {endNumbers.map(number => {
                    const end = endsByNumber.get(number);
                    return (
                      <td
                        key={number}
                        className={`${scoreCellBase} ${isCurrentEndColumn(number) ? 'bg-amber-100' : 'bg-white'}`}
                      >
                        {end ? endTileText(end, row.score(end), row.otherScore(end)) : ''}
                      </td>
                    );
                  })}
                  <td className={`${scoreCellBase} ${isCurrentExtraEndColumn ? 'bg-amber-100' : 'bg-white'}`}>
                    {hasExtraEnd
                      ? endTileText(
                          {
                            number: 9, team1Score: 0, team2Score: 0,
                            completed: extraEnds.some(end => end.completed),
                            team1ShotsThrown: 0, team2ShotsThrown: 0,
                          },
                          extraEndTotal(row.score),
                          extraEndTotal(row.otherScore),
                        )
                      : ''}
                  </td>
                  <td className={`${scoreCellBase} bg-[#d4d4d4] text-2xl`}>{row.total}</td>
                </tr>
              );
            })}
          </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/** Lightens (positive) or darkens (negative) a "#rrggbb" color by shifting each
 * channel toward white/black — used to build the glossy highlight/shadow shades a
 * curling rock icon's gradients need from just the one base team color. */
function shade(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const channel = (shift: number) => Math.max(0, Math.min(255, shift + amt));
  const r = channel((num >> 16) & 0xff);
  const g = channel((num >> 8) & 0xff);
  const b = channel(num & 0xff);
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}

/**
 * A curling rock viewed from above: a dark granite ring around a glossy colored
 * face with a raised handle down the middle — the same look a physical tote board's
 * magnets or a broadcast graphic uses, rather than a plain colored dot. `dimmed`
 * (already thrown) desaturates the face and handle to grey; `highlighted` (throwing
 * now) reads bigger and slowly spins in place — a moving stone catches the eye
 * without needing a static highlight color. `id` only needs to be unique among rocks
 * on this page, since it just namespaces this rock's own gradient defs.
 */
function CurlingRockIcon({ id, handleColor, dimmed, highlighted }:
  { id: string; handleColor: string; dimmed: boolean; highlighted: boolean }) {
  const ringGrad = `rock-ring-${id}`;
  const faceGrad = `rock-face-${id}`;
  const handleGrad = `rock-handle-${id}`;

  const faceBase = dimmed ? '#a1a1aa' : handleColor;
  const handleBase = shade(faceBase, dimmed ? -15 : -22);
  // Throwing now reads bigger and spins; already thrown reads smaller and muted —
  // size carries the same "in play vs. done" signal the grey/spin treatment does.
  const size = highlighted ? 38 : dimmed ? 22 : 30;

  return (
    <svg
      width={size} height={size} viewBox="0 0 100 100"
      className={`flex-shrink-0 transition-all ${highlighted ? 'animate-[spin_3s_linear_infinite]' : ''}`}
    >
      <defs>
        <radialGradient id={ringGrad} cx="35%" cy="30%" r="75%">
          <stop offset="0%" stopColor="#6b7280" />
          <stop offset="55%" stopColor="#3f3f46" />
          <stop offset="100%" stopColor="#18181b" />
        </radialGradient>
        <radialGradient id={faceGrad} cx="32%" cy="26%" r="85%">
          <stop offset="0%" stopColor={shade(faceBase, 32)} />
          <stop offset="50%" stopColor={faceBase} />
          <stop offset="100%" stopColor={shade(faceBase, -28)} />
        </radialGradient>
        <linearGradient id={handleGrad} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={shade(handleBase, 30)} />
          <stop offset="45%" stopColor={handleBase} />
          <stop offset="100%" stopColor={shade(handleBase, -20)} />
        </linearGradient>
      </defs>

      <circle cx="50" cy="50" r="48" fill={`url(#${ringGrad})`} stroke="#000000" strokeWidth="1" />
      <circle cx="50" cy="50" r="37" fill={`url(#${faceGrad})`} />
      <path
        d="M 50 19 C 58 19 61 24 61 30 L 61 61 C 61 70 56 77 50 81
           C 44 77 39 70 39 61 L 39 30 C 39 24 42 19 50 19 Z"
        fill={`url(#${handleGrad})`}
        stroke={shade(handleBase, -30)}
        strokeWidth="1"
      />
    </svg>
  );
}

/**
 * Every rock of the end currently being played, team 1 left-aligned and team 2
 * right-aligned in the same row — greyed once thrown, highlighted for the one
 * currently being recorded (the freshest entry in `currentShotHistory`).
 * Rocks-per-team mirrors ShotTracker's own rule: 6 for a 2-player (mixed doubles)
 * roster, 8 otherwise.
 *
 * Mixed doubles: 2 rocks total (1 per team, not 2 per team) are pre-placed on the
 * sheet before the end starts, not delivered by a player — ShotTracker never records
 * a shot for them, so `end.teamXShotsThrown` only counts actual deliveries (0-5).
 * PREPLACED_MIXED_DOUBLES_ROCKS shifts both the greyed-out count and which rock
 * position "currently throwing" points at, so each team's one preplaced stone always
 * reads as already down regardless of how many real shots have been recorded yet.
 *
 * Styled as a second small arena board directly under the scoreboard — light gray
 * body, black grid rule, top ad banner — rather than a plain card, so the two read as
 * one broadcast package. No team names or end number here: the scoreboard immediately
 * above already establishes both (left/right position and each rock's own handle
 * color carry the identification), so repeating them would just be noise.
 */
function RockTracker({ game }: { game: LiveGameDetailData }) {
  const endNumber = currentEndNumber(game);
  const end = endNumber === null ? undefined : game.ends.find(e => e.number === endNumber);
  if (!end) return null;

  const isMixedDoubles = game.team1Players.length === 2;
  const rocksPerTeam = isMixedDoubles ? 6 : 8;
  const PREPLACED_MIXED_DOUBLES_ROCKS = isMixedDoubles ? 1 : 0;
  const current = game.currentShotHistory[0];
  const numberFont = { fontFamily: "'Barlow Condensed', sans-serif" } as const;

  const teamGroup = (side: 'team1' | 'team2', color: RockColor | undefined, shotsThrown: number) => {
    const block = ROCK_COLOR_BLOCK[color ?? 'white'];
    const thrown = Math.min(rocksPerTeam, PREPLACED_MIXED_DOUBLES_ROCKS + shotsThrown);
    return (
      <div className="flex items-center gap-1.5">
        {Array.from({ length: rocksPerTeam }, (_, i) => i + 1).map(rockNumber => {
          const isThrown = rockNumber <= thrown;
          const isCurrent = current?.endNumber === endNumber && current.team === side
            && current.shotNumber + PREPLACED_MIXED_DOUBLES_ROCKS === rockNumber;
          return (
            <CurlingRockIcon
              key={rockNumber}
              id={`${side}-${rockNumber}`}
              handleColor={block.bg}
              dimmed={isThrown && !isCurrent}
              highlighted={isCurrent}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div
      className="rounded-md p-3 shadow-2xl mt-3"
      style={{ background: '#e5e5e5', border: '4px solid #1c1917' }}
    >
      {/* Top ad block — sponsor space, same reservation as the scoreboard's own
          banner; just the club's own name until this is sold to a sponsor. */}
      <div className="text-center py-2 border-2 border-b-0 border-black" style={{ background: '#1c1917' }}>
        <span className="text-white font-bold uppercase tracking-[0.2em] text-lg" style={numberFont}>
          Black Rock Curling Rock Tracker
        </span>
      </div>

      <div className="border-2 border-black bg-white px-3 py-3 flex items-center justify-between gap-3 flex-wrap">
        {teamGroup('team1', game.team1RockColor, end.team1ShotsThrown)}
        {teamGroup('team2', game.team2RockColor, end.team2ShotsThrown)}
      </div>
    </div>
  );
}

/** Same green/amber/red banding as BRC Scoring - Teams & Players, so a score reads the
 * same way wherever a viewer sees it. */
function scoreColor(value: number | undefined, scale: number): string {
  if (value === undefined) return 'text-blue-300/40';
  const pct = (value / scale) * 100;
  if (pct >= 80) return 'text-emerald-400';
  if (pct >= 50) return 'text-amber-400';
  return 'text-red-400';
}

function TeamScoreCard({ teamName, score }: { teamName: string; score: LiveGameTeamScore }) {
  return (
    <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-xl p-4">
      <h3 className="text-base font-semibold text-white mb-1">{teamName}</h3>
      <p className="text-xs text-slate-400 mb-3">Team score is the average of its players' scores.</p>
      <div className="flex items-baseline gap-2 mb-3">
        <span className={`text-3xl font-bold ${score.overallScore === undefined ? 'text-slate-500' : 'text-white'}`}>
          {score.overallScore !== undefined ? `${score.overallScore.toFixed(0)}%` : '—'}
        </span>
      </div>
      <div className="flex gap-6">
        <div>
          <p className="text-xs text-blue-300 mb-1">Weight Control</p>
          <p className={`text-base font-bold ${scoreColor(score.weightControlScore, 10)}`}>
            {score.weightControlScore !== undefined ? score.weightControlScore.toFixed(1) : '—'}
            <span className="text-xs text-blue-300 font-normal"> / 10</span>
          </p>
        </div>
        <div>
          <p className="text-xs text-blue-300 mb-1">Curl Control</p>
          <p className={`text-base font-bold ${scoreColor(score.curlControlScore, 10)}`}>
            {score.curlControlScore !== undefined ? score.curlControlScore.toFixed(1) : '—'}
            <span className="text-xs text-blue-300 font-normal"> / 10</span>
          </p>
        </div>
      </div>
    </div>
  );
}

function PlayerTable({ teamName, players }: { teamName: string; players: LiveGamePlayerScore[] }) {
  if (players.length === 0) return null;
  return (
    <div className="mb-6">
      <h3 className="text-sm font-semibold text-white mb-2">{teamName}</h3>
      <div className="overflow-x-auto">
        <table className="w-full bg-white/8 backdrop-blur-sm border border-white/15 rounded-xl overflow-hidden">
          <thead className="bg-white/5 border-b border-white/15">
            <tr>
              <th className="px-4 py-2 text-left text-xs font-semibold text-blue-300 uppercase tracking-wider">Player</th>
              <th className="px-4 py-2 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Shots</th>
              <th className="px-4 py-2 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Weight</th>
              <th className="px-4 py-2 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Curl</th>
              <th className="px-4 py-2 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Overall</th>
              <th className="px-4 py-2 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Traditional</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {players.map((player, idx) => (
              <tr key={player.playerName} className={idx % 2 === 0 ? 'bg-white/5' : ''}>
                <td className="px-4 py-2 whitespace-nowrap text-sm font-semibold text-white">{player.playerName}</td>
                <td className="px-4 py-2 whitespace-nowrap text-center text-sm text-white">{player.totalShots}</td>
                <td className="px-4 py-2 whitespace-nowrap text-center text-sm">
                  <span className={`font-semibold ${scoreColor(player.weightControlScore, 10)}`}>
                    {player.weightControlScore !== undefined ? player.weightControlScore.toFixed(1) : '—'}
                  </span>
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-center text-sm">
                  <span className={`font-semibold ${scoreColor(player.curlControlScore, 10)}`}>
                    {player.curlControlScore !== undefined ? player.curlControlScore.toFixed(1) : '—'}
                  </span>
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-center text-sm font-bold text-white">
                  {player.overallScore !== undefined ? `${player.overallScore.toFixed(0)}%` : '—'}
                </td>
                <td className="px-4 py-2 whitespace-nowrap text-center text-sm text-blue-300">
                  {player.accuracyPercent !== undefined ? `${player.accuracyPercent.toFixed(0)}%` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const SHOT_TYPE_LABELS: Record<ShotType, string> = {
  hogged: 'Hogged',
  'high-guard': 'High Guard',
  'mid-guard': 'Mid Guard',
  'tight-guard': 'Tight Guard',
  'top-twelve': 'Top Twelve',
  'top-eight': 'Top Eight',
  'top-four': 'Top Four',
  'tee-line': 'Tee Line',
  'back-four': 'Back Four',
  'back-eight': 'Back Eight',
  'back-twelve': 'Back Twelve',
  hack: 'Hack',
  board: 'Board',
  control: 'Control',
  normal: 'Normal',
  firm: 'Firm',
  peel: 'Peel',
};

const SHOT_RESULT_INFO: Record<string, { label: string; color: string }> = {
  excellent: { label: '4 pts', color: 'text-emerald-400' },
  good: { label: '3 pts', color: 'text-blue-400' },
  fair: { label: '2 pts', color: 'text-amber-400' },
  poor: { label: '1 pt', color: 'text-orange-400' },
  miss: { label: '0 pts', color: 'text-red-400' },
};

/** Good is the on-target outcome for both weight and curl; either miss direction gets
 * the same "off target" color — the live view doesn't need the asymmetric emphasis the
 * scoring bands themselves use. */
function weightLabel(weight: Weight): string {
  return weight === 'light' ? 'Light' : weight === 'heavy' ? 'Heavy' : 'Good';
}
function curlLabel(curl: CurlAssessment): string {
  return curl === 'under' ? 'Under' : curl === 'over' ? 'Over' : 'Good';
}
function onTargetColor(onTarget: boolean): string {
  return onTarget ? 'text-emerald-400' : 'text-amber-400';
}

/**
 * One shot slot's live broadcast card — see {@code CurrentShotService} on the backend
 * for the three moments this fills in: identity only at first ("plan in progress,
 * result not entered"), the plan once Analyze Shot is pressed, the result once Record
 * Shot is pressed.
 */
function CurrentShotCard({ shot, teamName }: { shot: CurrentShot; teamName: string }) {
  const resultInfo = shot.result !== undefined ? SHOT_RESULT_INFO[shot.result] : null;

  return (
    <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-xl p-4">
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <p className="text-sm font-semibold text-white">{shot.playerName}</p>
          <p className="text-xs text-blue-300">{teamName} • Shot {shot.shotNumber} • End {shot.endNumber}</p>
        </div>
        {resultInfo && <span className={`text-lg font-bold flex-shrink-0 ${resultInfo.color}`}>{resultInfo.label}</span>}
      </div>

      {shot.shotType !== undefined ? (
        <p className="text-xs text-slate-300 mb-2">
          Shot Plan: <span className="text-white font-medium">{SHOT_TYPE_LABELS[shot.shotType]}</span>
          {shot.expectedTime !== undefined && shot.expectedCurl !== undefined && (
            <span>
              {' | '}Expected Weight: {shot.expectedTime.toFixed(1)}s{' | '}
              Expected Curl: {shot.expectedCurl.toFixed(1)} ft
            </span>
          )}
        </p>
      ) : (
        <p className="text-xs text-blue-300/50 italic mb-2">Plan in progress…</p>
      )}

      {shot.weight !== undefined && shot.curlAssessment !== undefined ? (
        <p className="text-xs text-slate-300">
          Shot Execution:
          {shot.actualZone !== undefined && (
            <span className="text-white font-medium"> {SHOT_TYPE_LABELS[shot.actualZone]}</span>
          )}
          {' | '}Weight thrown <span className={onTargetColor(shot.weight === 'good')}>{weightLabel(shot.weight)}</span>
          {' | '}
          <span className={onTargetColor(shot.curlAssessment === 'good')}>{curlLabel(shot.curlAssessment)}</span> curl
        </p>
      ) : (
        <p className="text-xs text-blue-300/50 italic">Result not entered</p>
      )}
    </div>
  );
}

/** Current shot plus the two before it, newest first — shots go quickly, so the
 * rolling history stays visible even if a poll lands between two of them. */
function CurrentShotSection({ game }: { game: LiveGameDetailData }) {
  if (game.currentShotHistory.length === 0) return null;
  return (
    <div className="mt-4">
      <h2 className="text-lg font-semibold text-white mb-2">Shot Tracker</h2>
      <div className="space-y-2">
        {game.currentShotHistory.map((shot, idx) => (
          <div key={`${shot.endNumber}-${shot.team}-${shot.shotNumber}`} className={idx === 0 ? '' : 'opacity-50'}>
            <CurrentShotCard shot={shot} teamName={shot.team === 'team1' ? game.team1Name : game.team2Name} />
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * Team summary boxes and per-player tables, same figures as BRC Scoring - Teams &
 * Players — but computed server-side from aggregate scores only (see {@code
 * LiveGamesService}/{@code BrcScoring}), never from raw shot rows, since this reaches
 * any signed-in spectator regardless of account.
 */
function TeamPerformance({ game }: { game: LiveGameDetailData }) {
  const team1Players = game.players.filter(p => p.team === 'team1');
  const team2Players = game.players.filter(p => p.team === 'team2');

  if (game.players.length === 0) {
    return (
      <div className="mt-4">
        <h2 className="text-lg font-semibold text-white mb-2">BRC Statistics</h2>
        <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-xl p-6 text-center">
          <Users className="w-8 h-8 text-blue-300/50 mx-auto mb-2" />
          <p className="text-sm text-blue-300">No shots recorded yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4">
      <h2 className="text-lg font-semibold text-white mb-2">BRC Statistics</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <TeamScoreCard teamName={game.team1Name} score={game.team1Score} />
        <TeamScoreCard teamName={game.team2Name} score={game.team2Score} />
      </div>
      <PlayerTable teamName={game.team1Name} players={team1Players} />
      <PlayerTable teamName={game.team2Name} players={team2Players} />
    </div>
  );
}

export function LiveGameDetail() {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();

  const [game, setGame] = useState<LiveGameDetailData | null>(null);
  const [error, setError] = useState('');
  // Read inside the interval below instead of depending on `game` directly, so the
  // effect (and its setInterval) doesn't tear down and restart on every poll.
  const gameRef = useRef<LiveGameDetailData | null>(null);

  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;

    const load = () => {
      fetchLiveGameDetail(gameId)
        .then(result => {
          if (cancelled) return;
          gameRef.current = result;
          setGame(result);
          setError('');
        })
        .catch(caught => {
          if (cancelled) return;
          setError(caught instanceof ApiError ? caught.message : 'Could not load this game.');
        });
    };

    load();
    // Stop polling once the game is complete — nothing left to update.
    const interval = setInterval(() => {
      if (!gameRef.current || !gameRef.current.complete) load();
    }, REFRESH_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [gameId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-2xl font-bold text-white">Live Game</h1>
          <button
            onClick={() => navigate('/live-games')}
            className="flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/20 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Live Games
          </button>
        </div>

        {error && <p className="text-red-400 text-sm mb-4">{error}</p>}

        {!game ? (
          <p className="text-blue-300">Loading…</p>
        ) : (
          <div>
            <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-xl px-4 py-2 mb-3 flex items-center justify-between">
              <p className="text-sm text-blue-300/70">
                {[
                  game.curlingClub,
                  game.sheetNumber && `Sheet ${game.sheetNumber}`,
                  game.firstShotAt && formatStartTime(game.firstShotAt),
                ].filter(Boolean).join(' • ') || 'Location not set'}
              </p>
              {game.complete ? (
                <span className="text-xs font-semibold text-blue-300/70">FINAL</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs font-semibold text-red-400">
                  <Radio className="w-3 h-3 animate-pulse" />
                  LIVE
                </span>
              )}
            </div>

            <ScoreboardTable game={game} />
            <RockTracker game={game} />
            <CurrentShotSection game={game} />
            <TeamPerformance game={game} />
          </div>
        )}
      </div>
    </div>
  );
}
